// src/features/apps/AppBuildRequestUtils.ts
//
// Pure rules of the holder-requested build (the Status tab's "Build the app"
// card, 3.34.0; owner ruling 2026-09-23: the account holder — customer or
// reseller — may ask the farm for a build). The factory stays the authority:
// its POST /v1/builds is idempotent (a re-request while a build is in flight
// answers the same row) and admission-gated (unmet prerequisites come back as
// named 422 problems). These helpers only decide what the card SAYS before the
// click and how a refusal reads after it.

import {
  FACTORY_ACTIVE_BUILD_STATUSES,
  FACTORY_BUILD_REFUSALS,
  FACTORY_BUILD_STATUS_LABELS,
} from '@/lib/constants/appFactory';
import type { FactoryBuild, FactoryRelease } from '@/lib/types/AppFactory';
import type { ApiError } from '@/shared/api/ApiError';
import { AppFactoryUtils } from './AppFactoryUtils';

export interface BuildButtonInput {
  /** The parent app is Active. */
  appActive: boolean;
  /** The platform's own row (roku / app_cfg_platform) is Active. */
  platformActive: boolean;
  /** The app has its address (app_cfg_web) — builds fail closed without it. */
  hasAddress: boolean;
  /** The release the button would build; null = the feed lists none for the platform. */
  release: FactoryRelease | null;
  /** The platform's build the farm still owns, if any. */
  activeBuild: FactoryBuild | null;
  /** The Farm chip is stale (no worker heartbeat lately). */
  farmStale: boolean;
}

export interface BuildButtonState {
  disabled: boolean;
  /** The line under the button (why it is off, or the farm warning); null = nothing to say. */
  reason: string | null;
}

/** The card's sentences; `{platform}` is filled by the component. */
export const BUILD_BUTTON_REASONS = {
  APP_INACTIVE: 'Activate the app first.',
  PLATFORM_INACTIVE: 'Turn the {platform} app on in Settings first.',
  NO_ADDRESS: "Set up your app's address first.",
  NO_RELEASE: 'No app version is available to build yet.',
  BUILD_ACTIVE: 'A build is in progress ({status}).',
  FARM_OFFLINE: 'The build system is offline; the build starts when it returns.',
} as const;

/** How many refusal sentences one notice carries (the rest wait for the next click). */
const REFUSAL_SENTENCES_MAX = 2;

export class AppBuildRequestUtils {
  /**
   * The release the button builds: the newest one in the feed that lists the
   * platform (a release without a platform list — an older daemon — counts for
   * every platform). Newest by `created`, then by id.
   */
  static currentRelease(releases: FactoryRelease[], platform: string): FactoryRelease | null {
    const sorted = [...AppFactoryUtils.releasesForPlatform(releases, platform)].sort((a, b) =>
      a.created === b.created
        ? AppBuildRequestUtils.compareIdDesc(a.shellReleaseId, b.shellReleaseId)
        : a.created < b.created
          ? 1
          : -1,
    );
    return sorted[0] ?? null;
  }

  static isActiveStatus(status: string): boolean {
    return (FACTORY_ACTIVE_BUILD_STATUSES as readonly string[]).includes(status);
  }

  /** The platform's newest build the farm still owns; null = none in flight. */
  static activeBuild(builds: FactoryBuild[], platform: string): FactoryBuild | null {
    return (
      AppFactoryUtils.sortByCreatedDesc(
        AppFactoryUtils.buildsForPlatform(builds, platform).filter((b) =>
          AppBuildRequestUtils.isActiveStatus(b.status),
        ),
      )[0] ?? null
    );
  }

  /** Any build of the app (any platform) the farm still owns — the poll trigger. */
  static hasActiveBuild(builds: FactoryBuild[]): boolean {
    return builds.some((b) => AppBuildRequestUtils.isActiveStatus(b.status));
  }

  /**
   * The button's state, first reason wins: the app, the platform row, the
   * address, a release to build, a build already in flight. A stale farm never
   * disables the button — builds queue honestly and start when it returns —
   * it only adds the warning line.
   */
  static buttonState(input: BuildButtonInput): BuildButtonState {
    if (!input.appActive) return { disabled: true, reason: BUILD_BUTTON_REASONS.APP_INACTIVE };
    if (!input.platformActive) return { disabled: true, reason: BUILD_BUTTON_REASONS.PLATFORM_INACTIVE };
    if (!input.hasAddress) return { disabled: true, reason: BUILD_BUTTON_REASONS.NO_ADDRESS };
    if (!input.release) return { disabled: true, reason: BUILD_BUTTON_REASONS.NO_RELEASE };
    if (input.activeBuild) {
      const label = FACTORY_BUILD_STATUS_LABELS[input.activeBuild.status] ?? input.activeBuild.status;
      return {
        disabled: true,
        reason: BUILD_BUTTON_REASONS.BUILD_ACTIVE.replace('{status}', label),
      };
    }
    if (input.farmStale) return { disabled: false, reason: BUILD_BUTTON_REASONS.FARM_OFFLINE };
    return { disabled: false, reason: null };
  }

  /**
   * A refused request in the customer's words. A 422 with named problems
   * yields the first two sentences (a known token's sentence, else the
   * factory's own message for that problem); a top-level known code yields its
   * sentence; anything else (a 400, the gateway's bounded 5xx, the network)
   * keeps the error's message.
   */
  static refusalSentence(err: ApiError, platformLabel: string, supportContact: string): string {
    const fill = (s: string) =>
      s.replace(/\{platform\}/g, platformLabel).replace(/\{supportContact\}/g, supportContact);
    if (err.httpStatusCode === 422 && err.problems && err.problems.length > 0) {
      return err.problems
        .slice(0, REFUSAL_SENTENCES_MAX)
        .map((p) => fill(FACTORY_BUILD_REFUSALS[p.field] ?? p.message))
        .join(' ');
    }
    if (err.code && FACTORY_BUILD_REFUSALS[err.code]) return fill(FACTORY_BUILD_REFUSALS[err.code]);
    return err.message;
  }

  /** Snowflake ids are decimal strings: longer is newer, then lexical. */
  private static compareIdDesc(a: string, b: string): number {
    if (a.length !== b.length) return b.length - a.length;
    return a < b ? 1 : a > b ? -1 : 0;
  }
}
